/**
 * CodingStandards — manage the DS Coder chat context at three scopes (Personal,
 * Workspace, Global) across two sections:
 *  - Coding Standards: knowledge packs (standards, common errors, golden libs)
 *    applied when a repo of the matching stack is attached.
 *  - Rules: Cursor-style user-authored rules applied when a repo is attached
 *    (always, or gated by file globs).
 *
 * Everything edited here is merged at chat time (precedence user > workspace >
 * global) and injected into the coding-assistant system prompt. AI-assist
 * buttons draft content; the user reviews before saving (the AI never writes
 * directly).
 */
import { useMemo, useState } from "react";
import {
  BookMarked,
  Loader2,
  Plus,
  Save,
  ScrollText,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useDsCoderAdmin, useDsCoderPackMutations, useDsCoderPacks } from "@/hooks/useDsCoderPacks";
import { useDsCoderRuleMutations, useDsCoderRules } from "@/hooks/useDsCoderRules";
import { BUILTIN_DS_CODER_RULES } from "@/services/dsCoder/builtinProjectRules";
import { generatePackDraft } from "@/services/dsCoder/packAiAssist";
import { generateRuleDraft } from "@/services/dsCoder/ruleAiAssist";
import { KNOWLEDGE_PACKS } from "@/services/dsCoder/knowledgePacks";
import type { CommonError, GoldenLibrary } from "@/services/dsCoder/types";
import type { DsCoderPack, DsCoderPackInput, PackScope } from "@/types/dsCoderPacks";
import type { DsCoderRule, DsCoderRuleInput, RuleScope } from "@/types/dsCoderRules";

const KNOWN_TECH_IDS = Object.keys(KNOWLEDGE_PACKS);

const EMPTY_DRAFT: DsCoderPackInput = {
  techId: "",
  label: "",
  docsLibraryId: "",
  standards: [],
  commonErrors: [],
  goldenLibraries: [],
  isEnabled: true,
};

function toDraft(pack: DsCoderPack): DsCoderPackInput {
  return {
    techId: pack.techId,
    label: pack.label,
    docsLibraryId: pack.docsLibraryId ?? "",
    standards: [...pack.standards],
    commonErrors: pack.commonErrors.map((e) => ({ ...e })),
    goldenLibraries: pack.goldenLibraries.map((g) => ({ ...g })),
    isEnabled: pack.isEnabled,
  };
}

/* ------------------------------- Row editors ------------------------------ */

function StringListEditor({
  label,
  items,
  placeholder,
  onChange,
}: {
  label: string;
  items: string[];
  placeholder: string;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...items, ""])}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {items.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
      {items.map((value, i) => (
        <div key={i} className="flex items-start gap-2">
          <Textarea
            value={value}
            placeholder={placeholder}
            rows={2}
            className="min-h-0"
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function CommonErrorsEditor({
  items,
  onChange,
}: {
  items: CommonError[];
  onChange: (next: CommonError[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Common errors → fixes</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...items, { error: "", fix: "" }])}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {items.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="grid flex-1 gap-1.5 sm:grid-cols-2">
            <Input
              value={item.error}
              placeholder="Mistake / error"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...next[i], error: e.target.value };
                onChange(next);
              }}
            />
            <Input
              value={item.fix}
              placeholder="How to avoid / fix"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...next[i], fix: e.target.value };
                onChange(next);
              }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function GoldenLibrariesEditor({
  items,
  onChange,
}: {
  items: GoldenLibrary[];
  onChange: (next: GoldenLibrary[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Golden libraries</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...items, { need: "", use: "", avoid: "" }])}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {items.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="grid flex-1 gap-1.5 sm:grid-cols-3">
            <Input
              value={item.need}
              placeholder="Need (e.g. Date/time)"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...next[i], need: e.target.value };
                onChange(next);
              }}
            />
            <Input
              value={item.use}
              placeholder="Use (recommended)"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...next[i], use: e.target.value };
                onChange(next);
              }}
            />
            <Input
              value={item.avoid ?? ""}
              placeholder="Avoid (optional)"
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...next[i], avoid: e.target.value };
                onChange(next);
              }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------- Editor dialog ---------------------------- */

function PackEditorDialog({
  open,
  onOpenChange,
  scope,
  workspaceId,
  editingPack,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: PackScope;
  workspaceId: string | null;
  editingPack: DsCoderPack | null;
}) {
  const { toast } = useToast();
  const { create, update } = useDsCoderPackMutations(scope, workspaceId);
  const [draft, setDraft] = useState<DsCoderPackInput>(EMPTY_DRAFT);
  const [aiBusy, setAiBusy] = useState(false);
  const [initializedFor, setInitializedFor] = useState<string | null>(null);

  // Reset the draft whenever the dialog opens for a different pack.
  const editorId = editingPack?.id ?? "new";
  if (open && initializedFor !== editorId) {
    setDraft(editingPack ? toDraft(editingPack) : { ...EMPTY_DRAFT });
    setInitializedFor(editorId);
  }
  if (!open && initializedFor !== null) {
    setInitializedFor(null);
  }

  const patch = (p: Partial<DsCoderPackInput>) => setDraft((d) => ({ ...d, ...p }));

  const runAi = async () => {
    if (!draft.techId.trim() || !draft.label.trim()) {
      toast({
        title: "Add a tech id and label first",
        description: "The AI needs to know which technology to write standards for.",
        variant: "destructive",
      });
      return;
    }
    setAiBusy(true);
    try {
      const result = await generatePackDraft({
        techId: draft.techId.trim(),
        label: draft.label.trim(),
        existing:
          draft.standards.length || draft.commonErrors.length || draft.goldenLibraries.length
            ? {
                standards: draft.standards,
                commonErrors: draft.commonErrors,
                goldenLibraries: draft.goldenLibraries,
              }
            : undefined,
      });
      patch({
        standards: result.standards,
        commonErrors: result.commonErrors,
        goldenLibraries: result.goldenLibraries,
        docsLibraryId: result.docsLibraryId ?? draft.docsLibraryId,
      });
      toast({ title: "AI draft ready", description: "Review the suggestions, then Save." });
    } catch (e) {
      toast({
        title: "AI generation failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setAiBusy(false);
    }
  };

  const clean = (d: DsCoderPackInput): DsCoderPackInput => ({
    ...d,
    techId: d.techId.trim(),
    label: d.label.trim(),
    docsLibraryId: d.docsLibraryId?.trim() || null,
    standards: d.standards.map((s) => s.trim()).filter(Boolean),
    commonErrors: d.commonErrors.filter((e) => e.error.trim() && e.fix.trim()),
    goldenLibraries: d.goldenLibraries
      .filter((g) => g.need.trim() && g.use.trim())
      .map((g) => ({ ...g, avoid: g.avoid?.trim() || undefined })),
  });

  const save = async () => {
    if (!draft.techId.trim() || !draft.label.trim()) {
      toast({ title: "Tech id and label are required", variant: "destructive" });
      return;
    }
    const payload = clean(draft);
    try {
      if (editingPack) {
        await update.mutateAsync({ id: editingPack.id, patch: payload });
        toast({ title: "Pack updated" });
      } else {
        await create.mutateAsync(payload);
        toast({ title: "Pack created" });
      }
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Save failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingPack ? "Edit pack" : "New pack"}</DialogTitle>
          <DialogDescription>
            Standards, common errors, and golden libraries for a technology. These guide the AI
            whenever a matching repo is attached.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="techId">Tech id</Label>
              <Input
                id="techId"
                list="ds-coder-tech-ids"
                value={draft.techId}
                placeholder="e.g. react"
                disabled={Boolean(editingPack)}
                onChange={(e) => patch({ techId: e.target.value })}
              />
              <datalist id="ds-coder-tech-ids">
                {KNOWN_TECH_IDS.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                value={draft.label}
                placeholder="e.g. React"
                onChange={(e) => patch({ label: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="docs">Docs library id (Context7)</Label>
              <Input
                id="docs"
                value={draft.docsLibraryId ?? ""}
                placeholder="owner/project (optional)"
                onChange={(e) => patch({ docsLibraryId: e.target.value })}
              />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch
                id="enabled"
                checked={draft.isEnabled ?? true}
                onCheckedChange={(v) => patch({ isEnabled: v })}
              />
              <Label htmlFor="enabled">Enabled</Label>
            </div>
          </div>

          <div className="rounded-md border border-dashed p-3">
            <Button type="button" variant="secondary" size="sm" onClick={runAi} disabled={aiBusy}>
              {aiBusy ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1" />
              )}
              {draft.standards.length || draft.commonErrors.length || draft.goldenLibraries.length
                ? "Expand with AI"
                : "Generate with AI"}
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Drafts standards, common errors, and golden libraries for review. Nothing is saved
              until you press Save.
            </p>
          </div>

          <StringListEditor
            label="Coding standards"
            items={draft.standards}
            placeholder="e.g. Function components + hooks only; no class components."
            onChange={(next) => patch({ standards: next })}
          />
          <CommonErrorsEditor
            items={draft.commonErrors}
            onChange={(next) => patch({ commonErrors: next })}
          />
          <GoldenLibrariesEditor
            items={draft.goldenLibraries}
            onChange={(next) => patch({ goldenLibraries: next })}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ Standards tab ----------------------------- */

function StandardsManager() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { data: isAdmin } = useDsCoderAdmin();

  const [scope, setScope] = useState<PackScope>("user");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<DsCoderPack | null>(null);

  const workspaceId = currentWorkspaceId ?? null;
  const packsQuery = useDsCoderPacks(scope, workspaceId);
  const { update, remove } = useDsCoderPackMutations(scope, workspaceId);

  const canEdit = useMemo(() => {
    if (scope === "user") return Boolean(user);
    if (scope === "workspace") return Boolean(workspaceId);
    return isAdmin === true;
  }, [scope, user, workspaceId, isAdmin]);

  const openCreate = () => {
    setEditingPack(null);
    setEditorOpen(true);
  };
  const openEdit = (pack: DsCoderPack) => {
    setEditingPack(pack);
    setEditorOpen(true);
  };

  const toggleEnabled = async (pack: DsCoderPack, isEnabled: boolean) => {
    try {
      await update.mutateAsync({ id: pack.id, patch: { isEnabled } });
    } catch (e) {
      toast({
        title: "Update failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const deletePack = async (pack: DsCoderPack) => {
    if (!window.confirm(`Delete the ${pack.label} pack?`)) return;
    try {
      await remove.mutateAsync(pack.id);
      toast({ title: "Pack deleted" });
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const packs = packsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Stack-aware standards, common errors, and golden libraries applied when a matching GitHub
          repo is attached.
        </p>
        {canEdit && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add pack
          </Button>
        )}
      </div>

      <Tabs value={scope} onValueChange={(v) => setScope(v as PackScope)}>
        <TabsList>
          <TabsTrigger value="user">Personal</TabsTrigger>
          <TabsTrigger value="workspace">Workspace</TabsTrigger>
          <TabsTrigger value="global">Global</TabsTrigger>
        </TabsList>
      </Tabs>

      {scope === "workspace" && !workspaceId && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Select or create a workspace (sidebar → Switch workspace) to manage shared team packs.
          </CardContent>
        </Card>
      )}

      {scope === "global" && !canEdit && (
        <p className="text-xs text-muted-foreground">
          These are the organization-wide defaults. You can view them, but only DS Coder admins can
          edit them.
        </p>
      )}

      {(scope !== "workspace" || workspaceId) && (
        <div className="space-y-3">
          {packsQuery.isLoading ? (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </>
          ) : packsQuery.isError ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-destructive">
                {(packsQuery.error as Error)?.message ?? "Failed to load packs"}
              </CardContent>
            </Card>
          ) : packs.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No packs in this scope yet.
                {canEdit && " Add one, or generate it with AI."}
              </CardContent>
            </Card>
          ) : (
            packs.map((pack) => (
              <Card key={pack.id} className={pack.isEnabled ? "" : "opacity-60"}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      {pack.label}
                      <Badge variant="secondary" className="font-mono text-[10px]">
                        {pack.techId}
                      </Badge>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <Switch
                          checked={pack.isEnabled}
                          onCheckedChange={(v) => toggleEnabled(pack, v)}
                          aria-label="Enabled"
                        />
                      )}
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(pack)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePack(pack)}
                            aria-label="Delete pack"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground">
                  {pack.standards.length} standards · {pack.commonErrors.length} common errors ·{" "}
                  {pack.goldenLibraries.length} golden libraries
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {canEdit && (
        <PackEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          scope={scope}
          workspaceId={workspaceId}
          editingPack={editingPack}
        />
      )}
    </div>
  );
}

/* ------------------------------- Rules editor ----------------------------- */

const EMPTY_RULE: DsCoderRuleInput = {
  name: "",
  description: "",
  content: "",
  globs: "",
  alwaysApply: false,
  isEnabled: true,
};

function ruleToDraft(rule: DsCoderRule): DsCoderRuleInput {
  return {
    name: rule.name,
    description: rule.description,
    content: rule.content,
    globs: rule.globs,
    alwaysApply: rule.alwaysApply,
    isEnabled: rule.isEnabled,
  };
}

function RuleEditorDialog({
  open,
  onOpenChange,
  scope,
  workspaceId,
  editingRule,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: RuleScope;
  workspaceId: string | null;
  editingRule: DsCoderRule | null;
}) {
  const { toast } = useToast();
  const { create, update } = useDsCoderRuleMutations(scope, workspaceId);
  const [draft, setDraft] = useState<DsCoderRuleInput>(EMPTY_RULE);
  const [aiBusy, setAiBusy] = useState(false);
  const [initializedFor, setInitializedFor] = useState<string | null>(null);

  const editorId = editingRule?.id ?? "new";
  if (open && initializedFor !== editorId) {
    setDraft(editingRule ? ruleToDraft(editingRule) : { ...EMPTY_RULE });
    setInitializedFor(editorId);
  }
  if (!open && initializedFor !== null) {
    setInitializedFor(null);
  }

  const patch = (p: Partial<DsCoderRuleInput>) => setDraft((d) => ({ ...d, ...p }));

  const runAi = async () => {
    if (!draft.name.trim()) {
      toast({
        title: "Name the rule first",
        description: "The AI needs a rule name to draft its content.",
        variant: "destructive",
      });
      return;
    }
    setAiBusy(true);
    try {
      const result = await generateRuleDraft({
        name: draft.name.trim(),
        description: draft.description?.trim() || undefined,
        existingContent: draft.content?.trim() || undefined,
      });
      patch({
        content: result.content,
        description: result.description ?? draft.description,
        globs: result.globs ?? draft.globs,
        alwaysApply: result.alwaysApply ?? draft.alwaysApply,
      });
      toast({ title: "AI draft ready", description: "Review the rule, then Save." });
    } catch (e) {
      toast({
        title: "AI generation failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.content.trim()) {
      toast({ title: "Name and content are required", variant: "destructive" });
      return;
    }
    const payload: DsCoderRuleInput = {
      name: draft.name.trim(),
      description: draft.description?.trim() ?? "",
      content: draft.content.trim(),
      globs: draft.globs?.trim() ?? "",
      alwaysApply: draft.alwaysApply ?? false,
      isEnabled: draft.isEnabled ?? true,
    };
    try {
      if (editingRule) {
        await update.mutateAsync({ id: editingRule.id, patch: payload });
        toast({ title: "Rule updated" });
      } else {
        await create.mutateAsync(payload);
        toast({ title: "Rule created" });
      }
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Save failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingRule ? "Edit rule" : "New rule"}</DialogTitle>
          <DialogDescription>
            A rule the AI must follow while working on an attached repo. Applies always, or only to
            files matching the globs you set.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name"
                value={draft.name}
                placeholder="e.g. Prefer server components"
                onChange={(e) => patch({ name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rule-desc">Description</Label>
              <Input
                id="rule-desc"
                value={draft.description ?? ""}
                placeholder="Short summary (optional)"
                onChange={(e) => patch({ description: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rule-globs">File globs</Label>
              <Input
                id="rule-globs"
                value={draft.globs ?? ""}
                placeholder="**/*.ts, **/*.tsx (optional)"
                disabled={draft.alwaysApply ?? false}
                onChange={(e) => patch({ globs: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Applies when the attached repo has matching files. Leave empty for a general rule.
              </p>
            </div>
            <div className="flex flex-col justify-end gap-3 pb-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="rule-always"
                  checked={draft.alwaysApply ?? false}
                  onCheckedChange={(v) => patch({ alwaysApply: v })}
                />
                <Label htmlFor="rule-always">Always apply</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="rule-enabled"
                  checked={draft.isEnabled ?? true}
                  onCheckedChange={(v) => patch({ isEnabled: v })}
                />
                <Label htmlFor="rule-enabled">Enabled</Label>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-dashed p-3">
            <Button type="button" variant="secondary" size="sm" onClick={runAi} disabled={aiBusy}>
              {aiBusy ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1" />
              )}
              {draft.content.trim() ? "Improve with AI" : "Draft with AI"}
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Drafts the rule body from its name for review. Nothing is saved until you press Save.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rule-content">Rule content (markdown)</Label>
            <Textarea
              id="rule-content"
              value={draft.content}
              rows={12}
              placeholder={"e.g.\n- Use function components and hooks only.\n- Never fetch inside useEffect; use React Query."}
              onChange={(e) => patch({ content: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- Rules tab ------------------------------- */

function RulesManager() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { data: isAdmin } = useDsCoderAdmin();

  const [scope, setScope] = useState<RuleScope>("user");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DsCoderRule | null>(null);

  const workspaceId = currentWorkspaceId ?? null;
  const rulesQuery = useDsCoderRules(scope, workspaceId);
  const { update, remove } = useDsCoderRuleMutations(scope, workspaceId);

  const canEdit = useMemo(() => {
    if (scope === "user") return Boolean(user);
    if (scope === "workspace") return Boolean(workspaceId);
    return isAdmin === true;
  }, [scope, user, workspaceId, isAdmin]);

  const openCreate = () => {
    setEditingRule(null);
    setEditorOpen(true);
  };
  const openEdit = (rule: DsCoderRule) => {
    setEditingRule(rule);
    setEditorOpen(true);
  };

  const toggleEnabled = async (rule: DsCoderRule, isEnabled: boolean) => {
    try {
      await update.mutateAsync({ id: rule.id, patch: { isEnabled } });
    } catch (e) {
      toast({
        title: "Update failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const deleteRule = async (rule: DsCoderRule) => {
    if (!window.confirm(`Delete the "${rule.name}" rule?`)) return;
    try {
      await remove.mutateAsync(rule.id);
      toast({ title: "Rule deleted" });
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const rules = rulesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Cursor-style rules the AI follows while working on an attached repo. Applied always, or
          scoped to files matching your globs.
        </p>
        {canEdit && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add rule
          </Button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">Built-in (Deeply Smart V2)</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Sourced from AGENTS.md. Applied when an attached repo matches the Deeply Smart layout
            (not editable here). Your scoped rules below still layer on top.
          </p>
        </div>
        {BUILTIN_DS_CODER_RULES.map((rule) => (
          <Card key={rule.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  {rule.name}
                  <Badge variant="secondary" className="text-[10px]">
                    Built-in
                  </Badge>
                  {rule.globs.trim() ? (
                    <Badge variant="outline" className="font-mono text-[10px] max-w-full truncate">
                      {rule.globs.trim()}
                    </Badge>
                  ) : null}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground line-clamp-2">
              {rule.description.trim() || rule.content.trim() || "No content yet."}
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={scope} onValueChange={(v) => setScope(v as RuleScope)}>
        <TabsList>
          <TabsTrigger value="user">Personal</TabsTrigger>
          <TabsTrigger value="workspace">Workspace</TabsTrigger>
          <TabsTrigger value="global">Global</TabsTrigger>
        </TabsList>
      </Tabs>

      {scope === "workspace" && !workspaceId && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Select or create a workspace (sidebar → Switch workspace) to manage shared team rules.
          </CardContent>
        </Card>
      )}

      {scope === "global" && !canEdit && (
        <p className="text-xs text-muted-foreground">
          These are the organization-wide defaults. You can view them, but only DS Coder admins can
          edit them.
        </p>
      )}

      {(scope !== "workspace" || workspaceId) && (
        <div className="space-y-3">
          {rulesQuery.isLoading ? (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </>
          ) : rulesQuery.isError ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-destructive">
                {(rulesQuery.error as Error)?.message ?? "Failed to load rules"}
              </CardContent>
            </Card>
          ) : rules.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No rules in this scope yet.
                {canEdit && " Add one, or draft it with AI."}
              </CardContent>
            </Card>
          ) : (
            rules.map((rule) => (
              <Card key={rule.id} className={rule.isEnabled ? "" : "opacity-60"}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      {rule.name}
                      {rule.alwaysApply ? (
                        <Badge variant="secondary" className="text-[10px]">
                          Always
                        </Badge>
                      ) : rule.globs.trim() ? (
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {rule.globs.trim()}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">
                          On repo attach
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <Switch
                          checked={rule.isEnabled}
                          onCheckedChange={(v) => toggleEnabled(rule, v)}
                          aria-label="Enabled"
                        />
                      )}
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteRule(rule)}
                            aria-label="Delete rule"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground line-clamp-2">
                  {rule.description.trim() || rule.content.trim() || "No content yet."}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {canEdit && (
        <RuleEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          scope={scope}
          workspaceId={workspaceId}
          editingRule={editingRule}
        />
      )}
    </div>
  );
}

/* --------------------------------- Page ----------------------------------- */

export default function CodingStandards() {
  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <BookMarked className="h-6 w-6" /> Coding Standards &amp; Rules
        </h1>
        <p className="text-muted-foreground mt-1">
          What the AI coding assistant follows when a GitHub repo is attached. Merged at chat time as
          personal &gt; workspace &gt; global.
        </p>
      </div>

      <Tabs defaultValue="standards">
        <TabsList>
          <TabsTrigger value="standards" className="flex items-center gap-1.5">
            <BookMarked className="h-4 w-4" /> Coding Standards
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-1.5">
            <ScrollText className="h-4 w-4" /> Rules
          </TabsTrigger>
        </TabsList>
        <TabsContent value="standards" className="mt-4">
          <StandardsManager />
        </TabsContent>
        <TabsContent value="rules" className="mt-4">
          <RulesManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
